import { viomeApiInstance as client } from './ApiConfig';

import {
  deleteNotificationData,
  markAsReadData,
} from '../usecase/Notification/Notification.model';
import type { LoginFormValues, User } from '../usecase/Login/Login.model';
import type { ForgotPasswordParams } from '../usecase/ForgotPassword/ForgotPassword.model';
import {
  Address,
  InfoPostData,
  ConsentPostData,
  SignUpRequestWithKit,
  ValidateEmailData,
  MultiKitValidationData,
} from '../usecase/Register/Register.model';
import type {
  BookmarkSupplementData,
  FoodDetailParam,
  FoodProductQueryParam,
  GetAllFoodsParam,
  GetFoodRankResponse,
  IDailyServingDataResponse,
  IFilterResponse,
  IFoodMetaDetailResponse,
  ISaveSupplementResponse,
  ISupplementFilterResponse,
  SaveFoodData,
  SupplementDetailParam,
} from '../usecase/Nutrition/Nutrition.model';
import {
  ImproveScoreParams,
  IScoreLevel,
} from '../usecase/Health/Health.model';
import { ReportData, SaveFoodPreferenceData } from '../usecase/Misc/MiscModel';
import { SubmitQuestionnaireData } from '../usecase/Home/Home.model';

const auth = {
  login: (data: LoginFormValues) => {
    return client.post<LoginFormValues, User>(
      'api/authenticate/credentials',
      data,
    );
  },
  forgotPassword: (data: ForgotPasswordParams) => {
    return client.post('api/authenticate/resetPassword/initiate', data);
  },
  getUserProfile: () => client.get('/api/user/profile'),
  getRefreshToken: () => client.get('/api/authenticate/refreshToken'),
  logout: () => client.get('/api/authenticate/logout'),
};

const register = {
  getKitInfo: (kitId: string) => {
    return client.get(`api/register/kit/byBarcode/${kitId}`);
  },
  getCountries: () => {
    return client.get(`api/public/countryInfo`);
  },
  getStates: (stateCode: string | undefined) => {
    return client.get(`api/public/stateInfo/${stateCode}`);
  },
  validateEmail: (data: ValidateEmailData) => {
    return client.post('api/register/validateEmailWithKit', data);
  },
  validateAddress: (data: Address) => {
    return client.post('api/register/validateAddress', data);
  },
  confirmDetails: (data: InfoPostData) => {
    return client.post('api/register/validateGeoAndAgeForKit', data);
  },
  submitConsent: (data: ConsentPostData) => {
    return client.post('api/register/getConsentForms', data);
  },
  registerKit: (data: SignUpRequestWithKit) => {
    return client.post('api/register/newKit', data);
  },
  validateMultiKit: (data: MultiKitValidationData) => {
    return client.post(`api/register/canThisUserRegisterKit`, data);
  },
  registerMultiKit: (data: MultiKitValidationData) => {
    return client.post(`api/register/newKitForExistingUser`, data);
  },
};

const notification = {
  getNotification: () => {
    return client.get('/api/notification/v1/inbox/message?category=ALL');
  },
  markAsRead: (data: markAsReadData) => {
    return client.post('/api/notification/v1/inbox/markAsRead', data);
  },
  deleteNotification: (data: deleteNotificationData) => {
    return client.post('/api/notification/v1/inbox/delete', data);
  },
  getUnReadMessageCount: () => {
    return client.get('/api/notification/v1/inbox/unreadMessageCount');
  },
};

const nutrition = {
  getAllFood: (params?: GetAllFoodsParam) => {
    return client.get('/api/nutrition/v1/food/list', {
      params: Object.fromEntries(
        Object.entries(params ?? []).map(([key, values]) => [
          key,
          values?.join('#'),
        ]),
      ),
    });
  },
  saveFood: (data: SaveFoodData) => {
    return client.post<SaveFoodData, null>(
      '/api/nutrition/v1/food/bookmark',
      data,
    );
  },

  getFoodRank: () => {
    return client.get<GetFoodRankResponse>('/api/nutrition/v1/food/rank');
  },
  getMyNutritionTips: () => {
    return client.get('api/nutrition/v1/tips');
  },
  getSupplementList: (params?: GetAllFoodsParam) => {
    return client.get('api/nutrition/v1/supplement/list', {
      params: Object.fromEntries(
        Object.entries(params ?? []).map(([key, values]) => [
          key,
          values?.join('#'),
        ]),
      ),
    });
  },
  getDailyServingData: () => {
    return client.get<IDailyServingDataResponse>(
      'api/nutrition/v1/food/macrosAndServings',
    );
  },
  saveSupplement: (data: BookmarkSupplementData) => {
    return client.post<BookmarkSupplementData, ISaveSupplementResponse>(
      'api/nutrition/v1/supplement/bookmark',
      data,
    );
  },
  getFoodFilter: () => {
    return client.get<IFilterResponse>('/api/nutrition/v1/food/filters');
  },
  getSupplementFilter: (product_id: number) => {
    return client.get<ISupplementFilterResponse>(
      `/api/nutrition/v1/supplement/filters?product_id=${product_id}`,
    );
  },
  getFoodDetail: (param: FoodDetailParam) => {
    return client.get(`/api/nutrition/v1/food/details`, {
      params: {
        food_id: param.food_id,
      },
    });
  },
  getFoodMetaDetail: (param: FoodDetailParam) => {
    return client.get<IFoodMetaDetailResponse>(
      `/api/nutrition/v1/food/metaDetails`,
      {
        params: {
          food_id: param.food_id,
        },
      },
    );
  },
  getSupplementDetail: (param: SupplementDetailParam) => {
    return client.get(`/api/nutrition/v1/supplement/details`, {
      params: {
        ingredient_id: param.ingredient_id,
      },
    });
  },
  getProducts: (params?: FoodProductQueryParam) => {
    return client.get('/api/nutrition/v1/products', {
      params,
    });
  },
};

const education = {
  getEducationContent: (placeHolder: string) => {
    return client.get(`/api/educarousel/getData?placeholder=${placeHolder}`);
  },
  getEducationalVideos: (placeHolder: string) => {
    return client.get(
      `/api/educarousel/getPlaylistData?placeholder=${placeHolder}`,
    );
  },
};

const bootup = {
  getInitData: () => {
    return client.get('/api/bootup/getInit');
  },
};

const health = {
  getHealthZonesSummary: () => {
    return client.get('/api/score/v1/summary/healthZones');
  },
  getMyLongevity: () => {
    return client.get('/api/score/v1/summary/myLongevity');
  },
  getMyMicrobiomeProfiles: () => {
    return client.get('/api/score/v1/summary/myMicrobiomeProfiles');
  },
  getMyActiveMicrobes: (type: string) => {
    return client.get(
      `/api/score/v1/details/myActiveMicrobes?microbiome=${type}`,
    );
  },
  getMyMicrobiomeDetail: (type: string) => {
    return client.get(`/api/score/v1/details/microbiome?microbiome=${type}`);
  },
  getMyHealthConnectionsSummary: () => {
    return client.get('/api/score/v1/summary/healthConnections');
  },
  getMyBioAgeDetail: () => {
    return client.get('/api/score/v1/details/bioAge');
  },
  getMyHealthConnectionList: () => {
    return client.get('/api/score/v1/details/healthConnectionsList');
  },
  getCommonlyLinkedSymptoms: (scoreId: number) => {
    return client.get(
      `/api/score/v1/details/commonlyLinkedSymptoms?score_id=${scoreId}`,
    );
  },
  getDoctorsNotes: (level: IScoreLevel, scoreId: number) => {
    return client.get(
      `/api/score/v1/details/doctorsNote/${level.toLowerCase()}?score_id=${scoreId}`,
    );
  },
  getL4ConnectionList: (scoreId: number) => {
    return client.get(
      `/api/score/v1/details/healthConnections/l4?score_id=${scoreId}`,
    );
  },
  getScoreReference: (scoreId: number) => {
    return client.get(
      `/api/score/v1/details/scoreReference?score_id=${scoreId}`,
    );
  },
  getInflammagingDetail: () => {
    return client.get('/api/score/v1/details/inflammaging');
  },
  getHealthConnectionDetail: (conditionName: string) => {
    return client.get(
      `/api/score/v1/details/healthConnections/details?condition_name=${conditionName}`,
    );
  },
  getHealthConnectionL4Detail: (conditionName: string, scoreId: number) => {
    return client.get(
      `/api/score/v1/details/healthConnections/details/l4details?score_id=${scoreId}&condition_name=${conditionName}`,
    );
  },
  getHealthBoosterAndBlocker: (scoreId: number) => {
    return client.get(
      `/api/score/v1/summary/blockersAndBoosters?score_id=${scoreId}`,
    );
  },
  getHealthZoneDetail: (scoreId: number) => {
    return client.get(`/api/score/v1/details/level/l2?score_id=${scoreId}`);
  },
  getContributingScores: (level: IScoreLevel, scoreId?: number) => {
    return client.get(
      `/api/score/v1/details/contributingScores/${level.toLowerCase()}`,
      {
        params: {
          score_id: scoreId,
        },
      },
    );
  },
  getScoreDetails: (level: IScoreLevel, scoreId?: number) => {
    return client.get(`/api/score/v1/details/level/${level?.toLowerCase()}`, {
      params: {
        score_id: scoreId,
      },
    });
  },
  getImproveScoreSummary: (scoreId: number) => {
    return client.get(
      `/api/score/v1/details/level/l4/improveScore?score_id=${scoreId}`,
    );
  },
  getImproveScoreDetail: (params: ImproveScoreParams, level: IScoreLevel) => {
    return client.get(
      `/api/score/v1/details/level/${level.toLowerCase()}/improveScoreDetails`,
      {
        params,
      },
    );
  },
};

const misc = {
  saveFoodPreference: (data: SaveFoodPreferenceData) => {
    return client.post('/api/user/updateDietPreference', data);
  },
  sendReport: (data: ReportData) => {
    return client.post('/api/account/generateReport', data);
  },
  getSampleDetails: () => {
    return client.get('/api/account/listTests');
  },
  getSubscriptionStatus: () => {
    return client.get(
      '/api/nutrition/v1/products?with_subscription_status=true',
    );
  },
  getFeatureKeys: () => {
    return client.get('/api/user/featureKeys');
  },
};

const home = {
  getHealthSummary: () => {
    return client.get('/api/home/v1/postRnr/healthSummary');
  },
  getNutritionSummaryList: () => {
    return client.get('/api/home/v1/postRnr/nutritionSummary/list');
  },
  getNutritionSummaryDetail: (scoreId: number) => {
    return client.get(
      `/api/home/v1/postRnr/nutritionSummary/details?score_id=${scoreId}`,
    );
  },
  getNutritionSummaryDetailBloodSugar: () => {
    return client.get(
      '/api/home/v1/postRnr/nutritionSummary/details/bloodSugarResponse',
    );
  },
  getFeaturedFood: () => {
    return client.get('/api/home/v1/postRnr/featureFood');
  },
  getNutritionSummary: () => {
    return client.get('/api/home/v1/postRnr/nutritionSummary');
  },
  getkitStatus: () => {
    return client.get('/api/home/v1/preRnr/track');
  },
  getIntroQuestionnaires: () => {
    return client.get('/api/home/v1/preRnr/introQuestionnarie');
  },
  submitIntroQuestionnaire: (data: SubmitQuestionnaireData) => {
    return client.post<SubmitQuestionnaireData, null>(
      '/api/home/v1/preRnr/answers',
      data,
    );
  },
  getPlantVirusData: () => {
    return client.get(
      '/api/home/v1/postRnr/nutritionSummary/details/plantVirus',
    );
  },
  getJourneyStates: () => {
    return client.get('/api/home/v1/preRnr/journeyStates');
  },
  getPeekIntoData: (scoreId: number) => {
    return client.get(`/api/educarousel/getBBCardData?bbid=${scoreId}`);
  },
};

const shop = {
  getShopProductTests: () => {
    return client.get('/api/shop/v1/getAllTests');
  },
  getShopProductFormulas: () => {
    return client.get('/api/shop/v1/getUserProducts');
  },
  getShopProductPlans: () => {
    return client.get('/api/shop/v1/getAllPlans');
  },
  getShopifyMultipass: (url: string) => {
    return client.post('/api/shop/v1/getMultipassUrl', {
      url,
    });
  },
  getFormulaBenefits: (product_id: number) => {
    return client.get(`/api/shop/v1/getFormulaBenefits?product_id=${product_id}`);
  },
};

export {
  auth,
  register,
  notification,
  nutrition,
  education,
  bootup,
  health,
  misc,
  home,
  shop,
};
